import {useCallback, useEffect} from 'react'

import {Heading, Pagination} from '@primer/react'

import {announce} from '@github-ui/aria-live'
import {useSearchParams} from '@github-ui/use-navigate'

import type {AgentFinderCatalogData, AgentFinderResourceType} from '../../../../types'

import {GridSection} from '../GridSection/GridSection'
import {ResourceTypeFilter, getSelectedResourceTypes} from '../ResourceTypeFilter/ResourceTypeFilter'

import styles from './Content.module.css'

type Props = {
  catalogData: AgentFinderCatalogData
  resourceTypes: AgentFinderResourceType[]
}

export function Content({catalogData, resourceTypes}: Props) {
  const [searchParams, setSearchParams] = useSearchParams()

  const searchParamKey = 'search'
  const urlSearchQuery = searchParams.get(searchParamKey) || ''
  const hasSearchTerm = urlSearchQuery.trim().length > 0

  const currentPage = parseInt(searchParams.get('page') || '1', 10)
  const entries = catalogData.entries

  const selectedTypes = getSelectedResourceTypes(searchParams)
  const hasTypeFilter = selectedTypes.length > 0

  // Announce search results to screen readers for accessibility (WCAG 4.1.3)
  // Using a delay to ensure the live region is established before announcing
  useEffect(() => {
    let message: string

    if (hasSearchTerm) {
      const searchCount = catalogData.pagination.count
      message =
        searchCount === 0
          ? `No AI resources found matching "${urlSearchQuery}". Try a different search term.`
          : `${searchCount} result${searchCount === 1 ? '' : 's'}`
    } else {
      const totalCount = catalogData.pagination.total
      message = `${totalCount} AI resource${totalCount === 1 ? '' : 's'} available.`
    }

    // Delay announcement to ensure live region is ready after page navigation
    const timeoutId = setTimeout(() => {
      announce(message)
    }, 150)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [catalogData.pagination.count, catalogData.pagination.total, hasSearchTerm, urlSearchQuery])

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(current => {
        const newSearchParams = new URLSearchParams(current)
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) {
            newSearchParams.delete(key)
          } else {
            newSearchParams.set(key, value)
          }
        }
        return newSearchParams
      })
    },
    [setSearchParams],
  )

  const handlePageChange = useCallback(
    (event: React.MouseEvent, page: number) => {
      event.preventDefault()
      updateSearchParams({page: page.toString()})
    },
    [updateSearchParams],
  )

  const totalPages = catalogData.pagination.totalPages || 1

  const heading = hasSearchTerm ? 'Search results' : 'All AI resources'

  const resultCount = hasSearchTerm ? catalogData.pagination.count : catalogData.pagination.total

  const selectedTypeNoun = selectedTypes.length === 1 ? 'type' : 'types'

  return (
    <div className={styles.container}>
      <div>
        <div className={styles.toolbar}>
          <div className="d-flex flex-items-center">
            <Heading as="h2" className="f3 mr-2">
              {heading}
            </Heading>

            {entries.length > 0 && <span className="Counter">{resultCount}</span>}
          </div>

          <ResourceTypeFilter resourceTypes={resourceTypes} />
        </div>

        {entries.length > 0 ? <GridSection entries={entries} /> : null}

        {hasSearchTerm && entries.length === 0 && (
          <p className="color-fg-muted mb-0">
            No AI resources found matching &quot;{urlSearchQuery}&quot;. Try a different search term.
          </p>
        )}

        {!hasSearchTerm && hasTypeFilter && entries.length === 0 && (
          <p className="color-fg-muted mb-0">
            No resources match the selected {selectedTypeNoun}. Try adjusting your filters.
          </p>
        )}
      </div>

      {!hasSearchTerm && totalPages > 1 && (
        <div className={styles.pagination}>
          <Pagination
            pageCount={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            surroundingPageCount={2}
            aria-label="Agent Finder pagination"
          />
        </div>
      )}
    </div>
  )
}
